import React from 'react';
import { useVariant } from '../../contexts/VariantContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useDomainManager } from '../../config';
import { getDomainImageAuto, handleImageErrorWithFallback } from '../../utils/imageManager';

const VariantFeatures = () => {
  const { variant } = useVariant();
  const { brand, domainConfig } = useTheme();
  const domainManager = useDomainManager(brand?.id);

  // Check if features should be shown based on domain configuration
  if (!domainConfig?.showFeatures) {
    return null;
  }

  // Get domain-specific features using the unified configuration
  const getDomainFeatures = () => {
    const features = domainManager.getFeatures();
    if (features && features.items && features.items.length > 0) {
      // Add image URLs to each feature item
      const featuresWithImages = features.items.map((feature, index) => ({
        ...feature,
        image: getDomainImageAuto(brand.id, `feature-${index + 1}`, ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'])
      }));
      
        return {
        title: features.title,
        subtitle: features.subtitle,
        description: features.description,
        features: featuresWithImages
      };
    }
    
    // If no features are configured, return null to hide the section
    return null;
  };

  const featuresData = getDomainFeatures();
  
  // If no features are configured, don't render the component
  if (!featuresData) {
    return null;
  }
  
  const { title, subtitle, description, features } = featuresData;

  // Variant-specific styling
  const getVariantClasses = () => {
    switch (variant) {
      case 'minimal':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm hover:shadow-md',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-gray-400',
          iconBg: 'bg-gray-100',
          iconColor: 'text-gray-600'
        };
      case 'modern':
        return {
          container: 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
          card: 'bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10',
          text: 'text-white',
          textSecondary: 'text-purple-100',
          accent: 'text-purple-200',
          iconBg: 'bg-gradient-to-r from-purple-500 to-pink-500',
          iconColor: 'text-white'
        };
      case 'corporate':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm hover:shadow-md',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-blue-600',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600'
        };
      case 'creative':
        return {
          container: 'bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100',
          card: 'bg-white/80 backdrop-blur-sm border border-white/50 shadow-lg hover:shadow-xl',
          text: 'text-gray-900',
          textSecondary: 'text-gray-700',
          accent: 'text-orange-500',
          iconBg: 'bg-gradient-to-r from-orange-400 to-pink-400',
          iconColor: 'text-white'
        };
      case 'elegant':
        return {
          container: 'bg-gradient-to-br from-blue-50 via-white to-blue-100',
          card: 'bg-white/80 backdrop-blur-sm border border-blue-200/50 shadow-sm hover:shadow-lg',
          text: 'text-blue-900',
          textSecondary: 'text-blue-600',
          accent: 'text-blue-500',
          iconBg: 'bg-gradient-to-r from-blue-400 to-blue-500',
          iconColor: 'text-white'
        };
      case 'dark':
        return {
          container: 'bg-gray-900',
          card: 'bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10',
          text: 'text-white',
          textSecondary: 'text-gray-300',
          accent: 'text-purple-200',
          iconBg: 'bg-gradient-to-r from-blue-500 to-purple-500',
          iconColor: 'text-white'
        };
      default:
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm hover:shadow-md',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600',
          accent: 'text-blue-600',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600'
        };
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <div className={`py-24 ${variantClasses.container}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <span className={`inline-block py-1 px-3 text-xs font-semibold rounded-full ${
            variant === 'modern' ? 'bg-white/10 text-white border border-white/20' :
            variant === 'dark' ? 'bg-white/10 text-white border border-white/20' :
            variant === 'creative' ? 'bg-white/20 text-gray-800 border border-white/30' :
            variant === 'elegant' ? 'bg-slate-100 text-slate-600 border border-slate-200' :
            'bg-blue-50 text-blue-600 border border-blue-200'
          }`}>
            Features
          </span>
          <h2 className={`mt-3 text-3xl font-extrabold sm:text-4xl ${variantClasses.text}`}>
            {title}
          </h2>
          <p className={`mt-4 text-xl ${variantClasses.textSecondary}`}>
            {subtitle}
          </p>
          {description && (
            <p className={`mt-4 text-lg ${variantClasses.textSecondary}`}>
              {description}
            </p>
          )}
        </div>

        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group relative rounded-2xl shadow-lg transition-all duration-300 overflow-hidden ${
                variantClasses.card
              } ${
                variant === 'creative' ? 'hover:scale-105 transform hover:rotate-1' :
                variant === 'elegant' ? 'hover:scale-105' :
                'hover:scale-105'
              }`}
            >
              {/* Feature Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => handleImageErrorWithFallback(e, brand.id, `feature-${index + 1}`)}
                />
                
                {/* Overlay */}
                <div className={`absolute inset-0 ${
                  variant === 'modern' ? 'bg-gradient-to-t from-black/60 via-transparent to-transparent' :
                  variant === 'dark' ? 'bg-gradient-to-t from-black/60 via-transparent to-transparent' :
                  'bg-gradient-to-t from-black/40 via-transparent to-transparent'
                }`}></div>
                
                {/* Icon */}
                <div className={`absolute top-4 right-4 text-3xl rounded-full w-12 h-12 flex items-center justify-center ${
                  variant === 'modern' ? 'bg-white/20 backdrop-blur-sm' :
                  variant === 'dark' ? 'bg-white/20 backdrop-blur-sm' :
                  variant === 'creative' ? 'bg-white/30 backdrop-blur-sm' :
                  variant === 'elegant' ? 'bg-white/30 backdrop-blur-sm' :
                  'bg-white/20 backdrop-blur-sm'
                }`}>
                  {feature.icon}
                </div>
              </div>

              {/* Feature Content */}
              <div className="p-6">
                <h3 className={`text-xl font-bold mb-3 ${variantClasses.text}`}>
                  {feature.title}
                </h3>
                <p className={`leading-relaxed ${variantClasses.textSecondary}`}>
                  {feature.description}
                </p>
              </div>

              {/* Hover Effect */}
              <div className={`absolute inset-0 border-2 border-transparent rounded-2xl transition-colors duration-300 ${
                variant === 'modern' ? 'group-hover:border-purple-400/20' :
                variant === 'dark' ? 'group-hover:border-blue-400/20' :
                variant === 'creative' ? 'group-hover:border-orange-400/20' :
                variant === 'elegant' ? 'group-hover:border-amber-400/20' :
                'group-hover:border-blue-600/20'
              }`}></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VariantFeatures;
